// ISBN primitives for the /books catalog search. The server side has its
// own copy (services/books/src/isbn.ts); this one exists because the
// catalog filters the available books in the browser, and it additionally
// knows that an ISBN-10 and its ISBN-13 form are the same book.

/** Strips the hyphens and spaces ISBNs are printed with, uppercases the rest. */
export const normaliseISBN = (value: string): string =>
  value.trim().toUpperCase().replace(/[- ]/g, '')

/**
 * A 10- or 13-character string, digits only, with `X` allowed as the final
 * character of an ISBN-10. Shape only -- the check digit is not verified.
 */
export const isLikelyISBN = (value: string): boolean => {
  const cleaned = normaliseISBN(value)
  if (cleaned.length !== 10 && cleaned.length !== 13) return false

  for (let i = 0; i < cleaned.length; i++) {
    const char = cleaned[i]
    if (char >= '0' && char <= '9') continue
    if (cleaned.length === 10 && i === 9 && char === 'X') continue
    return false
  }

  return true
}

/** The ISBN-13 an ISBN-10 maps to: `978` + its first 9 digits + a fresh check digit. */
const isbn10To13 = (isbn10: string): string => {
  const body = `978${isbn10.slice(0, 9)}`

  let sum = 0
  for (let i = 0; i < 12; i++) {
    sum += (i % 2 === 0 ? 1 : 3) * (body.charCodeAt(i) - 0x30)
  }

  return `${body}${(10 - (sum % 10)) % 10}`
}

const ISBN10 = /^[0-9]{9}[0-9X]$/
const ISBN13 = /^[0-9]{13}$/

/**
 * Whether a query matches an identifier value, both taken to be
 * ISBN-shaped. Beyond plain equality, an ISBN-10 matches its ISBN-13 form
 * (records carry either), and a digit run shorter than a full ISBN is a
 * prefix search over the identifier.
 */
export const isbnMatches = (query: string, identifier: string): boolean => {
  const q = normaliseISBN(query)
  const id = normaliseISBN(identifier)
  if (q === '' || id === '') return false
  if (q === id) return true

  if (ISBN10.test(q) && ISBN13.test(id)) return isbn10To13(q) === id
  if (ISBN13.test(q) && ISBN10.test(id)) return isbn10To13(id) === q

  if (/^[0-9]+$/.test(q) && /^[0-9]+$/.test(id)) return id.includes(q)

  return false
}

/* ---------------------------------------------------------- catalog search */

/*
 * The catalog search operates on anything shaped like an AvailableBook
 * (services/books' GET /v1/downloads), declared structurally here so this
 * module stays free of the queries module's imports.
 */
export interface CatalogBook {
  title: string
  author: string
  identifiers: { type: string, value: string }[]
}

// Anna types ISBN identifiers as `isbn`, `isbn10`, `isbn13` or `isbn-13`
// (the kobo service matches all of them); anything else is a google/oclc/...
// id.
export const isISBNIdentifierType = (type: string): boolean =>
  type.trim().toLowerCase().includes('isbn')

/*
 * One query, three targets: the title and the author as plain
 * case-insensitive substrings, or the ISBNs -- where a 10-digit query
 * matches a 13-digit record and the other way round, since records carry
 * either form.
 */
export const matchesCatalogQuery = (book: CatalogBook, rawQuery: string): boolean => {
  const query = rawQuery.trim()
  if (query === '') return true

  if (isLikelyISBN(query) || /^[0-9Xx]+$/.test(query)) {
    return book.identifiers.some(
      identifier => isISBNIdentifierType(identifier.type) && isbnMatches(query, identifier.value)
    )
  }

  const needle = query.toLowerCase()
  return book.title.toLowerCase().includes(needle) || book.author.toLowerCase().includes(needle)
}
